import { Auth } from 'aws-amplify';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import UsersClient from 'src/clients/UsersClient';
import { useIsAssemblyBrandEnabled } from 'src/hooks/useIsAssemblyBrandEnabled';
import { useAppDispatch } from 'src/hooks/useStore';
import {
  OnboardingLogin,
  parseOnboardingLoginSubmitValues,
  trackOnboardingLoginEvent,
  useOnboardingLoginFormValidation,
} from 'src/legacy/components/Onboarding/OnboardingForms';
import { LegacyOnboardingPageHeader } from 'src/routes/onboarding/setup/components/LegacyOnboardingPageHeader';
import { useSetFormPageInfo } from 'src/routes/onboarding/setup/context/formPageInfoContext';
import {
  OnboardingFormValues,
  OnboardingStep,
  TrackedEventsType,
} from 'src/routes/onboarding/setup/types';
import { setAuthError } from 'src/store/auth/slice';
import { getAuthRequestInfo, GetSignInMetadata } from 'src/utils/AuthUtils';
import { CognitoUserWithAttributes } from 'src/utils/CognitoUtils';
import { ensureError } from 'src/utils/Errors';

export const CreateAccount = ({
  trackedEvents,
  onAnalyticsEventTracked,
  onAuthenticateUser,
}: {
  trackedEvents: TrackedEventsType;
  onAnalyticsEventTracked: (eventStep: OnboardingStep) => void;
  onAuthenticateUser: (user: CognitoUserWithAttributes) => void;
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { isAssemblyBrandEnabled } = useIsAssemblyBrandEnabled();

  const validationSchema = useOnboardingLoginFormValidation({});

  const setFormPageInfo = useSetFormPageInfo<OnboardingFormValues>();
  useEffect(() => {
    setFormPageInfo({
      submitHandler: async ({
        values: submitValues,
        formikHelpers,
        identifyUser,
      }) => {
        const parsedValues = parseOnboardingLoginSubmitValues(submitValues);

        try {
          let user: CognitoUserWithAttributes | null = null;

          if (!parsedValues.password) {
            // if the password is not set, then a google authed user might be logged in
            user = await Auth.currentAuthenticatedUser();
          } else {
            user = await Auth.signIn(
              parsedValues.email,
              parsedValues.password,
              GetSignInMetadata(undefined, await getAuthRequestInfo()),
            );
          }

          // if this is success email is used and password is correct
          // go to Create your portal account (pass email in next step)
          if (user) {
            onAuthenticateUser(user);
            if (user.challengeName === 'SOFTWARE_TOKEN_MFA') {
              formikHelpers.setValues(parsedValues);
              identifyUser(parsedValues);

              trackOnboardingLoginEvent(
                parsedValues,
                trackedEvents,
                onAnalyticsEventTracked,
              );

              return {
                navigationContext: { challengeName: user.challengeName },
              };
            } else {
              // set the onboarding values name and email attributes from the authenticated user.
              // Using the authenticated email is critical for Google OAuth: Google strips
              // plus-addressing (e.g. "aj+test@domain.com" → "aj@domain.com"), so the form
              // email may differ from the actual Cognito identity. If we don't sync them,
              // the backend creates a second Cognito user with the plus-addressed email,
              // which breaks post-onboarding auth (the Google session maps to the original
              // Cognito user, not the new one).
              const { given_name, family_name, email } = user.attributes;
              const newValues: OnboardingFormValues = {
                ...parsedValues,
                name: `${given_name} ${family_name}`,
                email: email ?? parsedValues.email,
              };
              formikHelpers.setValues(newValues);
              identifyUser(newValues);
            }
          }
        } catch (e) {
          // The error returned from Auth.signIn is an error from Cognito which
          // does not have code and looks like:
          /* {
           message: "User does not exist.",
           __type: "UserNotFoundException"
        }
        */
          const { message } = ensureError(e);
          const userNotFound = message === 'User does not exist.';
          if (userNotFound) {
            // when user is not found, validate email domain and if successful go to Create your portal account
            try {
              dispatch(setAuthError(''));
              await UsersClient.validateEmailDomain(parsedValues.email);
              trackOnboardingLoginEvent(
                parsedValues,
                trackedEvents,
                onAnalyticsEventTracked,
              );
              return;
            } catch (emailErr) {
              formikHelpers.setFieldError('email', message);
              throw new Error('There was an error while logging in');
            }
          }

          // user not logged in and error is NotAuthorizedException, so email IS being used but password is incorrect.
          // We need to show user an error that sign-in failed. Once they correctly auth we can actually
          // go to next step to create portal with existing user. This is important to prevent anyone
          // from just creating a portal with someone else's email without actually authenticating
          formikHelpers.setFieldError(
            'password',
            'Sign in failed, password is incorrect, try again.',
          );
          throw new Error('There was an error while logging in');
        }

        trackOnboardingLoginEvent(
          parsedValues,
          trackedEvents,
          onAnalyticsEventTracked,
        );
        return;
      },
      validationSchema,
    });
  }, [validationSchema]);

  return (
    <>
      <LegacyOnboardingPageHeader
        title={t(($) => $.page_onboarding.registration.title)}
        subtitle={t(($) =>
          isAssemblyBrandEnabled
            ? $.page_onboarding.registration.subtitle.assembly
            : $.page_onboarding.registration.subtitle.legacy,
        )}
      />
      <OnboardingLogin />
    </>
  );
};
